<template>
  <div class="network-rule" :key="updateCardKey">
    <div v-if="!selectedNetworkRule" class="no network-rule-card">
      <span>{{ $t('SelectNetworkRule')}}</span>
    </div>
    <div v-if="!!selectedNetworkRule" class="network-rule-info-container">
      <object-main-info
        :fields="fields"
        :identifier="cardObjectIdentifier"
        :identifier-label="$tc('Label', 1)"
        :editing-data="editingData"
        :show-edit-button="!creatingNetworkRule && userHasPermission('network_rule_update')"
        :card-label="cardLabel "
        :height="cardHeight"
        @toggle-edit="toggleEdit"
        @update-object="updateNetworkRule"
      />
      <network-rule-specific-info
        :network-rule-data="networkRule.data"
        :network-rule-id="networkRule.id"
        :editing-data="editingData"
        :requiredFieldsFilled="requiredFieldsAreFilled"
        :submit-button-label="submitButtonLabel"
        @update-specific-field="updateNetworkRuleSpecificField"
        @cancel-edit="discardChangesDialogIsOpen = true"
        @save-material="saveChangesDialogIsOpen = true"
      />
    </div>

    <loading-spinner
      :active.sync="loading"
      :is-full-page="false"
      color="#29BDF0"
      style="position: absolute;"
    />
    <dialog-choice
      :isOpen="saveChangesDialogIsOpen"
      :title="$t('SaveChanges')"
      :content="$t('SaveChangesContent')"
      @mainAction="saveObject"
      :mainActionLabel="$t('Confirm')"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="saveChangesDialogIsOpen = false"
      icon="mdi-content-save"
      iconColor="#29BDF0"
    />
    <dialog-choice
      :isOpen="discardChangesDialogIsOpen"
      :title="$t('DiscardChanges')"
      :content="$t('ClusterCreationConfirmation')"
      :mainActionLabel="$t('Confirm')"
      @mainAction="cancelEdit"
      :secondaryActionLabel="$t('Cancel')"
      @secondaryAction="discardChangesDialogIsOpen = false"
      icon="mdi-delete"
      iconColor="#29BDF0"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { bus } from "@/main.js";
import _ from 'lodash';
import utils from "@/utils";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import { objectManagementMixin } from "@/mixins/objectManagementMixin";
import { createEditPageMixin } from "@/mixins/management/createEditPageMixin";
import LoadingSpinner from "vue-loading-overlay";
import ObjectMainInfo from '@/newComponents/_Organisms/ObjectMainInfo';
import NetworkRuleSpecificInfo from '@/newComponents/SystemSettingsManagement/NetworkRules/NetworkRuleSpecificInfo';
import DialogChoice from '../../_Molecules/DialogChoice.vue';
import { unitConverterMixin } from "@/mixins/management/unitConverterMixin";
export default {
  name: "NetworkRule",

  components: {
    LoadingSpinner,
    DialogChoice,
    ObjectMainInfo,
    NetworkRuleSpecificInfo,
  },

  mixins: [checkPermissionsMixin, objectManagementMixin, createEditPageMixin, unitConverterMixin],

  props: {
    selectedNetworkRule: {
      type: Object,
    },
  },

  data() {
    return {
      isCreate: false,
      allTemplates: [],
      templateOptions: [],
      baseTemplateID: null,
      loading: false,
      networkRule: null,
      editingData: false,
      originalNetworkRule: {},
      specificRequiredFieldsAreFilled: false,
      dataToSend: {},
      actionToExecute: 'EDIT_NETWORK_RULES_TEMPLATE',
      submitButtonLabel: 'Save',
      cardLabel: 'EditNetworkRule',
      saveChangesDialogIsOpen: false,
      discardChangesDialogIsOpen: false,
      buttonStyling: {
        'padding': '0px',
        'drop-shadow': '0.85px 0.85px 1.75px rgba(0, 0, 0, 0.25)',
        'border-radius': '4px',
      },

      unitFields:[
        { name: "feeder_max_cable_length", unit:"m" },
        { name: "distribution_max_cable_length", unit:"m" },
        { name: "drop_max_cable_length", unit:"m" },
        { name: "vertical_distribution_max_cable_length", unit:"m" },
        { name: "vertical_drop_max_cable_length", unit:"m" }
      ],

      updateCardKey: 0,
    }
  },

  created() {
    this.loadBaseTemplateOptions();
  },

  watch: {
    selectedNetworkRule(value) {
      if (value !== null && Object.keys(value).length === 0) { //if network rule is an empty object for creation
        this.setCreateNetworkRuleVariables();
      } else if (!!value) { // if the network rule is a normal object to be edited
        this.setEditNetworkRuleVariables();
      } else if (value === null) { // if there is no object
        this.clearVariables();
      }
    },

    baseTemplateID(value) {
      if(value){
        let baseTemplate = this.allTemplates.find(
          (template) => template.id === value
        );
        this.networkRule.data = {...baseTemplate.data};
      }else{
        this.networkRule = {
          ...this.networkRule,
          data: {
            fibers_per_tube: null, // number of fibers per tube (must be multiple of 3)
            feeder_splitters_list: [], //array of splitters
            distribution_splitters_list: [], //array of splitters
            drop_splitters_list: [], //array of splitters
            vertical_splitters_list: [], //array of splitters
            subscriber_splitters_list: [], //array of splitters
            feeder_max_splitters: 0, //maximum number of splitters
            distribution_max_splitters: 0, //maximum number of splitters
            drop_max_splitters: 0, //maximum number of splitters
            vertical_max_splitters: 0, //maximum number of splitters
            subscriber_max_splitters: 0, //maximum number of splitters
            feeder_allow_fibers_out_splitters: false, //boolean
            distribution_allow_fibers_out_splitters: false, //boolean
            drop_allow_fibers_out_splitters: false, //boolean
            vertical_allow_fibers_out_splitters: false, //boolean
            subscriber_allow_fibers_out_splitters: false, //boolean
            feeder_cable_materials_list: [], //array of cable materials
            distribution_cable_materials_list: [], //array of cable materials
            drop_cable_materials_list: [], //array of cable materials
            vertical_drop_cable_materials_list: [], //array of cable materials
            vertical_distribution_cable_materials_list: [], //array of cable materials
            subscriber_cable_materials_list: [], //array of cable materials
            feeder_maintenance_mode: "fixed", //Percentage or fixed
            distribution_maintenance_mode: "fixed", //Percentage or fixed
            drop_maintenance_mode: "fixed", //Percentage or fixed
            vertical_maintenance_mode: "fixed", //Percentage or fixed
            subscriber_maintenance_mode: "fixed", //Percentage or fixed
            feeder_extra_mode: "fixed", //Percentage or fixed
            distribution_extra_mode: "fixed", //Percentage or fixed
            drop_extra_mode: "fixed", //Percentage or fixed
            vertical_extra_mode: "fixed", //Percentage or fixed
            subscriber_extra_mode: "fixed", //Percentage or fixed
            feeder_maintenance_value: undefined, //Number or percentage of maintenance fibers
            distribution_maintenance_value: undefined, //Number or percentage of maintenance fibers
            drop_maintenance_value: undefined, //Number or percentage of maintenance fibers
            vertical_maintenance_value: undefined, //Number or percentage of maintenance fibers
            subscriber_maintenance_value: undefined, //Number or percentage of maintenance fibers
            feeder_extra_value: undefined, //Number or percentage of extra  fibers
            distribution_extra_value: undefined, //Number or percentage of extra  fibers
            drop_extra_value: undefined, //Number or percentage of extra  fibers
            vertical_extra_value: undefined, //Number or percentage of extra  fibers
            subscriber_extra_value: undefined, //Number or percentage of extra  fibers
            feeder_closures_list: [], //array of closures
            distribution_closures_list: [], //array of closures
            drop_closures_list: [], //array of closures
            vertical_closures_list: [], //array of closures
            sdu_cross_splitter: false, // boolean
            mdu_cross_splitter: false, // boolean
            sdu_uses_splitter: false, // boolean
            mdu_point_2_point: false, // boolean
            mdu_classifier: 0, // boolean
            loss_value_cabinet: "0.00001", //Number
            loss_value_cable: "0.00001", //Number
            loss_value_harness_cable: "0.00001", //Number
            loss_value_drop_cable: "0.00001", //Number
            loss_value_splice: "0.00001", //Number
            loss_value_splitter_1_2: "0.00001", //Number
            loss_value_splitter_1_2_cascade: "0.00001", //Number
            loss_value_splitter_1_4: "0.00001", //Number
            loss_value_splitter_1_4_cascade: "0.00001", //Number
            loss_value_splitter_1_8: "0.00001", //Number
            loss_value_splitter_1_8_cascade: "0.00001", //Number
            loss_value_splitter_1_12: "0.00001", //Number
            loss_value_splitter_1_12_cascade: "0.00001", //Number
            loss_value_splitter_1_16: "0.00001", //Number
            loss_value_splitter_1_16_cascade: "0.00001", //Number
          },
        };
      }
    },

    editingData() {
      bus.$emit("toggle-create-edit-flag")
    },
  },

  computed: {
    requiredFieldsAreFilled() {
      const requiredFieldsFilled = !!this.networkRule.name
        && !!this.networkRule.data.fibers_per_tube
        && this.networkRule.data.fibers_per_tube > 0
        // && this.fibersPerTubeIsMultipleOf3 removed per request
        && this.lossValuesAreValid
      return requiredFieldsFilled;
    },

    fibersPerTubeIsMultipleOf3() {
      return this.networkRule.data && this.networkRule.data.fibers_per_tube
        ? (this.networkRule.data.fibers_per_tube % 3 === 0 || parseInt(this.networkRule.data.fibers_per_tube) === 1)
        : false
    },

    lossValuesAreValid() {
      let lossValues = Object.keys(this.networkRule.data)
        .filter(key => key.includes('loss_value')) // Filter keys that contain 'loss_value'
        .reduce((acc, key) => {
          acc[key] = this.networkRule.data[key]; // Add the key-value pair to the new object
          return acc;
        }, {});
      return Object.values(lossValues).every(value => !!value ? value >= 0.00001 : true); // checks if every loss value attribute that has a value greater than 0.00001
    },

    creatingNetworkRule() {
      return this.actionToExecute === 'CREATE_NETWORK_RULES_TEMPLATE';
    },

    fields() {
      let fields = [];
      if (this.networkRule !== null) {
        fields = [
          {
            icon: "subject",
            label: this.$i18n.t("Description"),
            value: this.networkRule.description,
            varName: 'description',
            inputType: 'textarea',
            disabled: false,
            required: false,
            size: '100%',
          },
        ];

        if (!this.editingData) {
          fields.push(
            {
              icon: "mdi-account",
              label: this.$i18n.t("CreatedAt"),
              value: this.networkRule.created_at,
              varName: 'created_at',
              inputType: 'text',
              disabled: false,
              required: false,
              size: '100%',
            },
            {
              icon: "mdi-calendar",
              label: this.$i18n.t("LastUpdate"),
              value: this.networkRule.updated_at,
              varName: 'updated_at',
              inputType: 'text',
              disabled: false,
              required: false,
              size: '100%',
            }
          )
        }

        if (this.editingData) {
          fields.splice(0, 0,
            {
              icon: "label",
              label: this.$i18n.tc("Label", 1),
              value: this.networkRule.name,
              varName: 'name',
              inputType: 'text',
              disabled: false,
              required: true,
              size: '100%',
            }
          )
        }

        if(this.isCreate){
          fields.unshift(
            {
              icon: "",
              label: `${this.$i18n.t("Copy")} ${this.$i18n.t("From")}`,
              value: this.baseTemplateID,
              varName: 'baseTemplateID',
              inputType: 'select',
              required: true,
              options: this.templateOptions,
              placeholder: "",
              clearable: true,
              size: '100%'
            },
          )
        }
      }
      return fields;
    },

    cardObjectIdentifier() {
      return this.networkRule ? this.networkRule.name : "";
    },

    cardHeight() {
      if(this.isCreate){
        return '30%'
      }else if (this.editingData) {
        return '25%'
      } else {
        return '25%'
      }
    },
  },

  methods: {
    ...mapActions([
      "CREATE_NETWORK_RULES_TEMPLATE",
      "EDIT_NETWORK_RULES_TEMPLATE",
      "FETCH_NETWORK_RULES_TEMPLATE",
      "FETCH_ALL_NETWORK_RULES_TEMPLATES"
    ]),

    loadBaseTemplateOptions() {
      this.FETCH_ALL_NETWORK_RULES_TEMPLATES({
        type: "type",
        value: "Calculation",
        queryParams: {
          paginate: false
        },
      })
        .then((templates) => {
          const processedTemplates = [];
          templates.data.forEach((template)=>{
            processedTemplates.push({
              id: template.id,
              label: template.name
            })
          });
          this.allTemplates = templates.data;
          this.templateOptions = processedTemplates;
        })
        .catch((error) => {
          console.error(error);
        });
    },

    async getNetworkRuleByUUID() {
      let uuid = this.selectedNetworkRule.uuid;

      this.loading = true;
      try {
        let networkRule = await this.FETCH_NETWORK_RULES_TEMPLATE(uuid);
        if(localStorage.getItem("measureSystem") === 'imperial'){
          this.unitFields.forEach(field => {
            if(networkRule.data[field.name]){
              networkRule.data[field.name] = this.convertFromMetricToImperial(field.unit, networkRule.data[field.name]).toFixed(field.precision ? field.precision :2);
            }
          });
        }
        this.networkRule = networkRule;
        this.originalNetworkRule = _.cloneDeep(this.networkRule);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    updateNetworkRule(updatedValue) {
      if(updatedValue.varName === 'baseTemplateID'){
        this[updatedValue.varName] = updatedValue.value;
        return;
      }
      this.networkRule[updatedValue.varName] = updatedValue.value;
    },

    updateNetworkRuleSpecificField(updatedValue) {
      this.networkRule.data[updatedValue.field.varName] = updatedValue.value;
    },

    cancelEdit() {
      this.discardChangesDialogIsOpen = false;
      this.editingData = false;
      if (this.networkRule.uuid) {
        this.networkRule = _.cloneDeep(this.originalNetworkRule);
      } else {
        this.$emit('clear-selected-item');
      }
    },

    toggleEdit() {
      if (this.editingData) {
        this.discardChangesDialogIsOpen = true;
      } else {
        this.editingData = !this.editingData
      }
    },

    async saveObject() {
      this.saveChangesDialogIsOpen = false;
      try {
        this.loading = true;

        this.createDataToSend();

        if (Object.keys(this.dataToSend).length > 0) {
          let data = this.dataToSend;
          if (!this.creatingNetworkRule) {
            data = {
              uuid: this.networkRule.uuid,
              updatedFields: this.dataToSend
            }
          }
          await this.$store.dispatch(this.actionToExecute, data);
        }

        this.editingData = false;

        if (!this.creatingNetworkRule) {
          this.getNetworkRuleByUUID();
          this.$toasted.global.toast_success(this.$i18n.t('NetworkRuleUpdatedSuccessfully'));
        } else {
          this.$toasted.global.toast_success(this.$i18n.t('NetworkRuleCreatedSuccessfully'));
        }

        bus.$emit('reload-network-rules');
      } catch (error) {
        console.error(error);
        this.handleError(error);
      } finally {
        this.loading = false;
        this.dataToSend = {};
        this.baseTemplateID = null;
      }
    },

    /** Fills the object to send to the server by putting a watcher in each variable of the object */
    createDataToSend() {
      Object.keys(this.networkRule).forEach(key => {
        if (this.networkRule[key] !== this.originalNetworkRule[key]) {
          this.dataToSend[key] = this.networkRule[key];
        }
      });

      if (!this.creatingNetworkRule) {
        delete this.dataToSend.data;
        Object.keys(this.networkRule.data).forEach(key => {
          if (this.valuesAreDifferent(this.networkRule.data[key], this.originalNetworkRule.data[key])) {

            if (!this.dataToSend.hasOwnProperty("data")) {
              this.dataToSend.data = {};
            }
            let unitField = this.unitFields.find(field => field.name === key)
            if(unitField && localStorage.getItem("measureSystem") === 'imperial'){
              this.dataToSend.data[key] = this.convertFromImperialToMetric(unitField.unit, this.networkRule.data[key]).toFixed(unitField.precision ? unitField.precision : 2);
            } else {
              this.dataToSend.data[key] = this.networkRule.data[key];
            }
          }
        });
      }

      if (this.creatingNetworkRule) {
        this.dataToSend.type = "Calculation";
      }
    },

    setCreateNetworkRuleVariables() {
      this.networkRule = {
        name: "",
        description: "",
        data: {
          fibers_per_tube: null, // number of fibers per tube (must be multiple of 3)
          feeder_splitters_list: [], //array of splitters
          distribution_splitters_list: [], //array of splitters
          drop_splitters_list: [], //array of splitters
          vertical_splitters_list: [], //array of splitters
          subscriber_splitters_list: [], //array of splitters
          feeder_max_splitters: 0, //maximum number of splitters
          distribution_max_splitters: 0, //maximum number of splitters
          drop_max_splitters: 0, //maximum number of splitters
          vertical_max_splitters: 0, //maximum number of splitters
          subscriber_max_splitters: 0, //maximum number of splitters
          feeder_allow_fibers_out_splitters: false, //boolean
          distribution_allow_fibers_out_splitters: false, //boolean
          drop_allow_fibers_out_splitters: false, //boolean
          vertical_allow_fibers_out_splitters: false, //boolean
          subscriber_allow_fibers_out_splitters: false, //boolean
          feeder_cable_materials_list: [], //array of cable materials
          distribution_cable_materials_list: [], //array of cable materials
          drop_cable_materials_list: [], //array of cable materials
          vertical_drop_cable_materials_list: [], //array of cable materials
          vertical_distribution_cable_materials_list: [], //array of cable materials
          subscriber_cable_materials_list: [], //array of cable materials
          feeder_maintenance_mode: "fixed", //Percentage or fixed
          distribution_maintenance_mode: "fixed", //Percentage or fixed
          drop_maintenance_mode: "fixed", //Percentage or fixed
          vertical_maintenance_mode: "fixed", //Percentage or fixed
          subscriber_maintenance_mode: "fixed", //Percentage or fixed
          feeder_extra_mode: "fixed", //Percentage or fixed
          distribution_extra_mode: "fixed", //Percentage or fixed
          drop_extra_mode: "fixed", //Percentage or fixed
          vertical_extra_mode: "fixed", //Percentage or fixed
          subscriber_extra_mode: "fixed", //Percentage or fixed
          feeder_maintenance_value: undefined, //Number or percentage of maintenance fibers
          distribution_maintenance_value: undefined, //Number or percentage of maintenance fibers
          drop_maintenance_value: undefined, //Number or percentage of maintenance fibers
          vertical_maintenance_value: undefined, //Number or percentage of maintenance fibers
          subscriber_maintenance_value: undefined, //Number or percentage of maintenance fibers
          feeder_extra_value: undefined, //Number or percentage of extra  fibers
          distribution_extra_value: undefined, //Number or percentage of extra  fibers
          drop_extra_value: undefined, //Number or percentage of extra  fibers
          vertical_extra_value: undefined, //Number or percentage of extra  fibers
          subscriber_extra_value: undefined, //Number or percentage of extra  fibers
          feeder_closures_list: [], //array of closures
          distribution_closures_list: [], //array of closures
          drop_closures_list: [], //array of closures
          vertical_closures_list: [], //array of closures
          sdu_cross_splitter: false, // boolean
          mdu_cross_splitter: false, // boolean
          sdu_uses_splitter: false, // boolean
          mdu_point_2_point: false, // boolean
          mdu_classifier: 0, // boolean
          loss_value_cabinet: "0.00001", //Number
          loss_value_cable: "0.00001", //Number
          loss_value_harness_cable: "0.00001", //Number
          loss_value_drop_cable: "0.00001", //Number
          loss_value_splice: "0.00001", //Number
          loss_value_splitter_1_2: "0.00001", //Number
          loss_value_splitter_1_2_cascade: "0.00001", //Number
          loss_value_splitter_1_4: "0.00001", //Number
          loss_value_splitter_1_4_cascade: "0.00001", //Number
          loss_value_splitter_1_8: "0.00001", //Number
          loss_value_splitter_1_8_cascade: "0.00001", //Number
          loss_value_splitter_1_12: "0.00001", //Number
          loss_value_splitter_1_12_cascade: "0.00001", //Number
          loss_value_splitter_1_16: "0.00001", //Number
          loss_value_splitter_1_16_cascade: "0.00001", //Number
        },
      };
      this.originalNetworkRule = {
        name: '',
        description: ''
      };
      this.actionToExecute = 'CREATE_NETWORK_RULES_TEMPLATE';
      this.submitButtonLabel = 'Create';
      this.cardLabel = 'NewTemplate';
      this.isCreate = true;
      this.editingData = true;
    },

    setEditNetworkRuleVariables() {
      this.getNetworkRuleByUUID();
      this.actionToExecute = 'EDIT_NETWORK_RULES_TEMPLATE';
      this.submitButtonLabel = 'Save';
      this.cardLabel = 'EditNetworkRule';
      this.isCreate = false;
    },

    clearVariables() {
      this.networkRule = null;
      this.originalNetworkRule = null;
      this.editingData = false;
    },

    handleError(error) {
      let errorHandle = utils.errorHandler(error);

      if (typeof errorHandle === "object") {
      } else if (typeof errorHandle === "string") {
        this.$toasted.global.toast_error(errorHandle);
      } else {
        this.$toasted.global.toast_error(this.$i18n.t('ErrorInOperation'))
      }
    },
  }
}
</script>

<style scoped>
.network-rule {
  height: 100%;
  max-height: 100%;
  background-color: #fff;
  position: relative;
  border-radius: 8px;
}

.permissionMatrixDiv {
  margin-top: 16px;
  padding-bottom: 16px;
}

.network-rule-card {
  height: 100%;
}

.network-rule-card {
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.25);
  border-radius: 8px;
}

.no.network-rule-card {
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
}

.no.network-rule-card > span {
  color: #29BDF0;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  line-height: normal;
}

.network-rule-info-container {
  gap: 8px;
  display: flex;
  flex-direction: column;
  max-height: 100%;
  height: 100%;
}

.network-rule-logo {
  background-color: white;
  border-radius: 8px;
  height: 40px;
  width: 40px;
  box-shadow: 1.75px 1.75px 4.75px 0px rgba(0, 0, 0, 0.25);
  margin-right: 16px;
  margin-left: 2px;
}

.edit-image-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 10px 0;
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/newComponents/SystemSettingsManagement/NetworkRules/NetworkRule.vue